import type { TileLayer, GeoJSON, MarkerClusterGroup, LayerGroup } from 'leaflet';
import L from 'leaflet';
import 'leaflet.markercluster';
import type { FeatureCollection } from 'geojson';
import { config } from '../config';
import { linzAttribution, osmAttribution } from './layers'; // Import attributions

type OverlayLayer = TileLayer | GeoJSON | MarkerClusterGroup | LayerGroup;

export type Overlays = Record<string, OverlayLayer>; // Use string index for dynamic layers

interface DynamicLayerConfig {
    dataset: string;
    topic: string;
}

const DYNAMIC_LAYER_CONFIG: Record<string, DynamicLayerConfig> = {
    'Beaches': { dataset: '', topic: 'beach' },
    'Bays': { dataset: '', topic: 'bay' },
    'Islands': { dataset: '', topic: 'island' },
    'Others': { dataset: '', topic: 'other' },
    'Lights': { dataset: 'lights', topic: 'light' },
    'Howick': { dataset: 'howick', topic: '' },
    'Maraetai': { dataset: 'maraetai', topic: '' },
    'VHF': { dataset: 'vhf', topic: '' }
};

// Override the internal method for generating spiderfy points
// @ts-ignore
const originalGeneratePointsCircle = L.MarkerCluster.prototype._generatePointsCircle;
// @ts-ignore
L.MarkerCluster.include({
    _generatePointsCircle: function (count: number, centerPt: L.Point) {
        const points = originalGeneratePointsCircle.call(this, count, centerPt);
        const angleOffset = 73 * Math.PI / 180;
        for (let i = 0; i < points.length; i++) {
            const p = points[i];
            const x = p.x - centerPt.x;
            const y = p.y - centerPt.y;
            const rotatedX = x * Math.cos(angleOffset) - y * Math.sin(angleOffset);
            const rotatedY = x * Math.sin(angleOffset) + y * Math.cos(angleOffset);
            points[i] = L.point(centerPt.x + rotatedX, centerPt.y + rotatedY);
        }
        return points;
    }
});

function createDividerLayer(): LayerGroup {
    const dividerLayers = L.layerGroup();
    let latlngsNS: L.LatLngExpression[] = [
        [-36., 175],
        [-37.77, 175],
    ];
    let latlngsEW: L.LatLngExpression[] = [
        [-36.8333, 174.42],
        [-36.8333, 175.92],
    ];
    L.polyline(latlngsNS, {color: 'red'}).addTo(dividerLayers);
    L.polyline(latlngsEW, {color: 'red'}).addTo(dividerLayers);
    return dividerLayers;
}

export function createOverlays(dynamicLayers: string[] = []): Overlays {
    const overlays: Overlays = {
        'Open Maps' : L.layerGroup([
            L.tileLayer(
                'https://tiles.openseamap.org/seamark/{z}/{x}/{y}.png',
                { 
                    maxZoom: 25,
                    attribution: 'Map data: &copy; <a href="http://www.openseamap.org">OpenSeaMap</a> contributors',
                    pane: 'topTilePane' // Use the custom pane
                }
            ),
            L.tileLayer(
                'https://{s}.tiles.openrailwaymap.org/standard/{z}/{x}/{y}.png',
                { 
                    maxZoom: 25,
                    attribution: 'Map data: &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors | Map style: &copy; <a href="https://www.OpenRailwayMap.org">OpenRailwayMap</a> (<a href="https://creativecommons.org/licenses/by-sa/3.0/">CC-BY-SA</a>)',
                    pane: 'topTilePane' // Use the custom pane
                }
            )
        ]),
        'LINZ Gazetteer' : createLazyOverlay("gaz", linzAttribution),
        'LINZ Lights' : createLazyOverlay("lights", linzAttribution),
        'OSM Gazetteer' : createLazyOverlay("osm", osmAttribution),
        'Tangata Whenua' : createLazyOverlay("tw", linzAttribution),
        'Extra' : createLazyOverlay("xtr"),
        'CG Units' : createLazyOverlay("units"),
        'Divider': createDividerLayer(),
    };

    // Add dynamic layers if they are specified in the URL
    Object.keys(DYNAMIC_LAYER_CONFIG).forEach(name => {
        if (dynamicLayers.includes(name)) {
            // We use the name as the ID for now, fetchOverlayGeoJSON will look up the config
            overlays[name] = createLazyOverlay(name);
        }
    });

    return overlays;
}

export interface LazyOverlay extends MarkerClusterGroup {
    overlayId?: string;
    attribution?: string;
    isLoading?: boolean;
    isScheduled?: boolean; // Flag for pending request
}

function createLazyOverlay(id: string, attribution?: string): LazyOverlay {
    const layerGroup = L.markerClusterGroup({
        spiderfyDistanceMultiplier: 4,
        spiderLegPolylineOptions: { weight: 3.0, color: '#800', opacity: 0.9 },
        maxClusterRadius: 40,
        zoomToBoundsOnClick: false,
        iconCreateFunction: function (cluster) {
            const count = cluster.getChildCount();
            const children = cluster.getAllChildMarkers();
            
            // Check if all children are at the same location (approx)
            const firstLatLng = children[0].getLatLng();
            const isSameLocation = children.every(child => {
                const latLng = child.getLatLng();
                return Math.abs(latLng.lat - firstLatLng.lat) < 0.001 && 
                       Math.abs(latLng.lng - firstLatLng.lng) < 0.001;
            });

            const labels = children.map(child => {
                const name = (child.options.icon?.options as any)?.html || 'none';
                return name
                    .replace(/ /g, '&nbsp;')
                    .replace(/'/g, '')
                    ;
            }) ;
            const uniqueLabels = [...new Set(labels)].slice(0, 3);
            const label = uniqueLabels.join('<br>') || 'Cluster';

            let c = ' marker-cluster-';
            if (count < 10) c += 'small';
            else if (count < 100) c += 'medium';
            else c += 'large';
            
            // Only show label if markers are at the same location
            const labelHtml = isSameLocation ? `<div class="cluster-label">${label}</div>` : '';

            return L.divIcon({
                html: `
      <div class="cluster-wrapper">
        <div class="marker-cluster${c}">
          <div><span>${count}</span></div>
        </div>
        ${labelHtml}
      </div>
    `,
                className: '',
                iconSize: [20, 20],
            });
        },
    }) as LazyOverlay;

    layerGroup.overlayId = id;
    layerGroup.attribution = attribution;
    layerGroup.isLoading = false;
    layerGroup.isScheduled = false;

    layerGroup.on('add', function() {
        if (this.attribution && (this as any)._map) {
            (this as any)._map.attributionControl.addAttribution(this.attribution);
        }
    });

    layerGroup.on('remove', function() {
        if (this.attribution && (this as any)._map) {
            (this as any)._map.attributionControl.removeAttribution(this.attribution);
        }
    });

    layerGroup.on('clusterclick', (e) => {
        e.layer.spiderfy();
    });

    layerGroup.on('spiderfied', (e) => {
        e.markers.forEach((marker: any) => {
            const el = marker._icon;
            if (el) el.classList.add('spiderfied-highlight');
        });
    });
    layerGroup.on('unspiderfied', (e) => {
        e.markers.forEach((marker: any) => {
            const el = marker._icon;
            if (el) el.classList.remove('spiderfied-highlight');
        });
    });

    return layerGroup;
}

export async function loadOverlayData(layer: LazyOverlay, map: L.Map, filter: string, unit: string = '', level: string = '', mode: string = '') {
    // If a request for this layer is already in flight, schedule a new one and exit.
    if (layer.isLoading) {
        layer.isScheduled = true;
        return;
    }

    layer.isLoading = true;

    try {
        layer.clearLayers();
        const data = await fetchOverlayGeoJSON(layer.overlayId!, map, filter, unit, level, mode);

        if (data instanceof L.MarkerClusterGroup) {
            data.eachLayer((l) => {
                layer.addLayer(l);
            });
        }
    } finally {
        layer.isLoading = false;
        // If another request came in while this one was running, run it now.
        if (layer.isScheduled) {
            layer.isScheduled = false;
            // Use a timeout to avoid deep recursion and allow the UI to update.
            setTimeout(() => loadOverlayData(layer, map, filter, unit, level, mode), 0);
        }
    }
}

async function fetchOverlayGeoJSON(
    overlayName: string,
    map: L.Map,
    filter: string,
    unit: string,
    level: string,
    mode: string
): Promise<FeatureCollection | L.MarkerClusterGroup> {
    const bounds = map.getBounds();
    
    let dataset = overlayName;
    let topic = '';

    // Check if this is a configured dynamic layer
    if (DYNAMIC_LAYER_CONFIG[overlayName]) {
        dataset = DYNAMIC_LAYER_CONFIG[overlayName].dataset;
        topic = DYNAMIC_LAYER_CONFIG[overlayName].topic;

        // Special handling for Lights: if no unit/level, use dataset='lights' and topic=''
        if (overlayName === 'Lights' && !unit && !level) {
            // dataset is already 'lights' from config
            topic = '';
        }
    }

    // Special case for 'CG Units' layer
    if (overlayName === 'units') {
        if (mode === 'training') {
            topic = 'unit';
        }
    }

    const payload = {
        overlayName,
        extent: {
            south: bounds.getSouth(),
            north: bounds.getNorth(),
            west: bounds.getWest(),
            east: bounds.getEast(),
        },
        unit: unit,
        topic: topic,
        level: level,
        filter: filter,
        dataset: dataset,
    };

    let layerGroup = L.markerClusterGroup();

    // console.log('Fetching overlay data for:', { overlayName, filter, unit, level, extent: payload.extent });

    const response: any = await fetch(`${config.serverBaseUrl}/api/poi-data`, {
        method: "POST",                       // <- POST instead of GET
        headers: {
            "Content-Type": "application/json"  // <- Tell server it's JSON
        },
        body: JSON.stringify(payload)         // <- Convert JS object to JSON string
    });

    if (!response.ok) {
        console.error("Failed to fetch overlay data:", response.statusText);
        throw new Error(`Server returned ${response.status}`);
    }

    const data = await response.json();
    // console.log('Received overlay data:', data.features ? data.features.length : 0, 'features for', overlayName);


    if (data && data.features) {
        data.features.forEach((feature: any) => {
            if (feature.geometry && feature.geometry.type === "Point") {
                const coordinates = [
                    feature.geometry.coordinates[1],
                    feature.geometry.coordinates[0]
                ] as L.LatLngTuple;
                const waypointName = feature.properties?.name || "Unnamed";
                const waypointId = feature.properties?.id || feature.properties?.name;
                const waypointHoverText = feature.properties?.hoverText || '';
                const linked = waypointHoverText.includes('http')? ' linked': '';
                const  label =
                    overlayName === 'Howick' // Use capitalized names now
                    || overlayName === 'Maraetai' ?
                        `${waypointId}:${waypointName}` : `${waypointName}`;
                const textOnlyIcon = L.divIcon({
                    className: `text-only-icon default ${linked}`,
                    html: `${label}`, // The text to display
                    iconSize: undefined, // No icon size to ensure it auto-sizes to the text
                });

                let waypointMarker = L.marker(coordinates, {icon: textOnlyIcon}) as any;
                waypointMarker.groupName = overlayName;
                if (linked) {
                    waypointMarker.bindPopup(waypointHoverText, {
                        autoPan: true,
                        autoPanPadding: [50, 50],
                    });
                } else {
                    waypointMarker.bindTooltip(waypointHoverText, {
                        permanent: false,
                        direction: "top",
                        className: "custom-tooltip",
                    });
                }
                waypointMarker.addTo(layerGroup);
            }
        });
    }

    return layerGroup;
}
